import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, RefreshCw, Sparkles, Calendar, User, Bug, ChevronDown, ChevronUp, Download, Cpu, Globe, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { GameSelector, GameBadge } from "@/components/GameSelector";
import { SlideToConfirm } from "@/components/SlideToConfirm";

export default function PatchNotes() {
  const { user, isAuthenticated } = useAuth();
  const [patchNotes, setPatchNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [version, setVersion] = useState("");
  const [expandedNote, setExpandedNote] = useState(null);
  const [selectedGame, setSelectedGame] = useState("STAR_ATLAS_UE5");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchPatchNotes = async (game) => {
    try {
      setLoading(true);
      const gameParam = game ? `?game=${game}` : '';
      const response = await axios.get(`${API}/patch-notes${gameParam}`);
      setPatchNotes(response.data);
    } catch (error) {
      console.error("Error fetching patch notes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatchNotes(selectedGame);
  }, [selectedGame]);

  const handleGameChange = (game) => {
    setSelectedGame(game);
  };

  const handleGenerate = async () => {
    if (!version.trim()) {
      toast.error("Please enter a version number");
      return;
    }

    try {
      setGenerating(true);
      const response = await axios.post(`${API}/patch-notes/generate`, {
        version: version.trim(),
        game: selectedGame,
        since_date: null // Generate from all fixed bugs for now
      }, { withCredentials: true });
      
      toast.success(`Patch notes generated! ${response.data.bug_count} bugs included.`);
      setVersion("");
      fetchPatchNotes(selectedGame);
    } catch (error) {
      console.error("Error generating patch notes:", error);
      toast.error(error.response?.data?.detail || "Failed to generate patch notes");
    } finally {
      setGenerating(false);
    }
  };

  const userRole = user?.role || "reporter";
  const canGenerate = isAuthenticated && (userRole === "admin" || userRole === "developer");
  const canDelete = canGenerate;
  const gameName = selectedGame === "STAR_ATLAS_HOLOSIM" ? "Holosim" : "UE5";

  const handleDeletePatchNote = async (noteId) => {
    try {
      await axios.delete(`${API}/patch-notes/${noteId}`, { withCredentials: true });
      toast.success("Patch note deleted");
      setDeleteConfirm(null);
      fetchPatchNotes(selectedGame);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete patch note");
    }
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="patch-notes-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          Patch Notes
        </h1>
        <div className="cyber-line mt-2" />
        <p className="text-muted-foreground mt-2 font-body text-sm">
          AI-generated patch notes from resolved bug reports
        </p>
      </div>

      {/* Game Selector */}
      <div className="mb-6">
        <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-3 block">
          Select Game
        </label>
        <GameSelector 
          selectedGame={selectedGame} 
          onGameChange={handleGameChange}
          size="large"
        />
      </div>

      {/* Generate New Patch Notes - Admin/Developer Only */}
      {canGenerate && (
        <Card className="bg-card border-border/40 border-l-4 border-l-primary mb-8">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Generate {gameName} Patch Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground font-mono uppercase mb-2 block">
                  Version Number
                </label>
                <Input
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder={selectedGame === "STAR_ATLAS_HOLOSIM" ? "e.g., v3.1.230" : "e.g., 2026.02.28.001"}
                  className="bg-black/20 border-border/50"
                  data-testid="version-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !version.trim()}
                  className="font-heading font-bold uppercase tracking-wider"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate
                    </>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground font-mono mt-3">
              AI will analyze all Fixed {gameName} bugs and generate professional patch notes
            </p>
          </CardContent>
        </Card>
      )}

      {/* Patch Notes List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : patchNotes.length > 0 ? (
        <div className="space-y-4">
          {patchNotes.map((note) => (
            <Card key={note.id} className="bg-card border-border/40 overflow-hidden">
              <CardHeader 
                className="border-b border-border/40 bg-muted/20 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-lg text-foreground">
                          Version {note.version}
                        </h3>
                        {/* Game Badge */}
                        {note.game && note.game !== "ALL" && (
                          <GameBadge game={note.game} size="small" />
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(note.generated_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bug className="w-3 h-3" />
                          {note.bug_count} fixes
                        </span>
                        {note.generated_by && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {note.generated_by}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(note.patch_notes);
                        toast.success("Copied to clipboard!");
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(deleteConfirm === note.id ? null : note.id);
                        }}
                        className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-patch-note-btn-${note.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {expandedNote === note.id ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {expandedNote === note.id && (
                <CardContent className="p-6">
                  <div className="prose prose-invert max-w-none">
                    <pre className="font-body text-sm text-foreground whitespace-pre-wrap bg-black/20 p-4 rounded-sm border border-border/40 overflow-x-auto">
                      {note.patch_notes}
                    </pre>
                  </div>
                </CardContent>
              )}

              {/* Slide to delete */}
              {deleteConfirm === note.id && canDelete && (
                <div className="px-4 pb-4">
                  <SlideToConfirm
                    onConfirm={() => handleDeletePatchNote(note.id)}
                    label="Slide to delete patch note"
                    variant="destructive"
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-card border-border/40">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-muted-foreground mb-2">
              No {gameName} Patch Notes Yet
            </h3>
            <p className="text-sm text-muted-foreground font-body">
              {canGenerate 
                ? `Generate patch notes from fixed ${gameName} bugs using the form above.`
                : "Patch notes will appear here when developers generate them."}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
